import { createContext, type ReactNode, useCallback, useMemo, useState } from "react";

export type EditorMode = "text" | "markdown";

export interface EditorModeContextValue {
  editorMode: EditorMode;
  setEditorMode: (mode: EditorMode) => void;
}

const STORAGE_KEY = "cheatsheetz-editor-mode";

function getStoredMode(): EditorMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "text" || stored === "markdown") {
      return stored;
    }
  } catch {
    // localStorage may not be available in tests / SSR
  }
  return "markdown";
}

export const EditorModeContext = createContext<EditorModeContextValue | null>(null);

export function EditorModeProvider({ children }: { children: ReactNode }) {
  const [editorMode, setEditorModeState] = useState<EditorMode>(getStoredMode);

  const setEditorMode = useCallback((mode: EditorMode) => {
    setEditorModeState(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // localStorage may not be available in tests / SSR
    }
  }, []);

  const value = useMemo(
    () => ({ editorMode, setEditorMode }),
    [editorMode, setEditorMode],
  );

  return (
    <EditorModeContext.Provider value={value}>{children}</EditorModeContext.Provider>
  );
}
